import { Palette, Sun, Moon, Monitor, Trees, Sparkles, Wind, Leaf, Pause, Gem, CircleOff } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLiving } from '../contexts/LivingContext';
import { useI18n } from '../i18n';
import ExperienceSwitcher from './ExperienceSwitcher';

function Seg({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button
            type="button"
            aria-pressed={active}
            data-testid={testid}
            onClick={onClick}
            className={`flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 py-2.5 px-1 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                active
                    ? 'bg-brand-green text-white shadow-sm'
                    : 'bg-brand-card border border-brand-border text-brand-ink2 hover:bg-brand-bg2'
            }`}
        >
            {Icon && <Icon size={15} strokeWidth={1.8} className="shrink-0" />}
            <span className="truncate">{label}</span>
        </button>
    );
}

export default function ThemeSwitcher() {
    const { palette, mode, setPalette, setMode } = useTheme();
    const { motion, setMotion, holo, setHolo, phase, season } = useLiving();
    const { t, locale } = useI18n();
    const da = locale === 'da';

    return (
        <section className="mb-8" data-testid="theme-switcher">
            <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3 inline-flex items-center gap-1">
                <Palette size={11} /> {t('theme.title')}
            </h2>
            <div className="bg-brand-card border border-brand-border rounded-2xl p-4 space-y-4">
                <div>
                    <div className="text-xs text-brand-ink3 mb-2">{da ? 'Visning i GrowDaily og Eden' : 'GrowDaily and Eden view'}</div>
                    <ExperienceSwitcher />
                    <p className="text-[11px] text-brand-ink3 mt-2 leading-relaxed">
                        {t('experience.description')}
                    </p>
                </div>
                <div>
                    <div className="text-xs text-brand-ink3 mb-2">{t('theme.palette')}</div>
                    <div className="flex gap-2">
                        <Seg active={palette === 'classic'} onClick={() => setPalette('classic')} icon={Sun} label={t('theme.classic')} testid="theme-palette-classic" />
                        <Seg active={palette === 'forest'} onClick={() => setPalette('forest')} icon={Trees} label={t('theme.forest')} testid="theme-palette-forest" />
                    </div>
                </div>
                <div>
                    <div className="text-xs text-brand-ink3 mb-2">{t('theme.mode')}</div>
                    <div className="flex gap-2">
                        <Seg active={mode === 'live'} onClick={() => setMode('live')} icon={Sparkles} label={t('theme.live')} testid="theme-mode-live" />
                        <Seg active={mode === 'light'} onClick={() => setMode('light')} icon={Sun} label={t('theme.light')} testid="theme-mode-light" />
                        <Seg active={mode === 'dark'} onClick={() => setMode('dark')} icon={Moon} label={t('theme.dark')} testid="theme-mode-dark" />
                        <Seg active={mode === 'system'} onClick={() => setMode('system')} icon={Monitor} label={t('theme.system')} testid="theme-mode-system" />
                    </div>
                    {mode === 'live' && (
                        <p data-testid="theme-live-status" className="mt-2 text-xs text-brand-ink3 leading-relaxed">
                            {t('theme.live_hint')}.{' '}
                            <span className="text-brand-green font-medium">{t('theme.now')}: {t(`live.phase.${phase}`)} · {t(`live.season.${season}`)}</span>
                        </p>
                    )}
                </div>
                <div>
                    <div className="text-xs text-brand-ink3 mb-2">{t('theme.motion')}</div>
                    <div className="flex gap-2">
                        <Seg active={motion === 'full'} onClick={() => setMotion('full')} icon={Wind} label={t('theme.motion_full')} testid="theme-motion-full" />
                        <Seg active={motion === 'calm'} onClick={() => setMotion('calm')} icon={Leaf} label={t('theme.motion_calm')} testid="theme-motion-calm" />
                        <Seg active={motion === 'off'} onClick={() => setMotion('off')} icon={Pause} label={t('theme.motion_off')} testid="theme-motion-off" />
                    </div>
                    <p className="mt-2 text-xs text-brand-ink3">{t('theme.motion_hint')}</p>
                </div>
                <div>
                    <div className="text-xs text-brand-ink3 mb-2">{t('theme.holo')}</div>
                    <div className="flex gap-2">
                        <Seg active={holo === 'on'} onClick={() => setHolo('on')} icon={Gem} label={t('theme.holo_on')} testid="theme-holo-on" />
                        <Seg active={holo === 'off'} onClick={() => setHolo('off')} icon={CircleOff} label={t('theme.holo_off')} testid="theme-holo-off" />
                    </div>
                </div>
            </div>
        </section>
    );
}
